import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, uploadFormData, openAuthedFile } from "@/api/client";
import {
  ClipboardList,
  Copy,
  Check,
  ExternalLink,
  Loader2,
  Paperclip,
  Download,
  ShieldCheck,
  Shield,
  Bot,
  Play,
  FlaskConical,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { ENTRY_TASK_KIND_LABELS } from "@ckgo2/shared/types";

/**
 * Entry paperwork queue for a departure (Block 5.4 Řez 1) — arrival cards & visas the
 * agency files per traveller. The list self-(re)generates on load (lazy ensure on the
 * API); "Copy filing sheet" puts the manual tahák on the clipboard as TSV (passport
 * role gated + audited server-side).
 */

interface EntryReadiness {
  passport: boolean;
  flight: boolean;
  hotel: boolean;
  contact: boolean;
  ready: boolean;
  missing: string[];
}

interface EntryTaskRow {
  task: {
    id: string;
    kind: "arrival_card" | "visa";
    status: "open" | "filed" | "failed" | "skipped";
    arrivalDate: string;
    windowOpensAt: string | null;
    referenceCode: string | null;
    artifactR2Key: string | null;
    artifactFileName: string | null;
    filedAt: string | null;
    notes: string | null;
    channel: "manual" | "auto" | null;
    attempts: number;
    lastError: string | null;
  };
  participantName: string;
  orderNumber: string | null;
  consentAt: string | null;
  readiness: EntryReadiness;
}

interface EntryGroup {
  countryCode: string;
  countryName: string;
  kind: "arrival_card" | "visa";
  windowOpensAt: string | null;
  portalUrl: string | null;
  groupMax: number | null;
  windowDays: number | null;
  autoChannel: "manual" | "browser" | null;
  autoEnabled: boolean;
  autoAvailable: boolean;
  tasks: EntryTaskRow[];
}

const STATUS_STYLES: Record<string, string> = {
  open: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  filed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  skipped: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

function windowChip(group: EntryGroup) {
  if (!group.windowOpensAt) {
    return (
      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
        Window unknown — set it in Country KB
      </span>
    );
  }
  const today = new Date().toISOString().slice(0, 10);
  const open = group.windowOpensAt <= today;
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs font-medium ${
        open
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
          : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
      }`}
    >
      {open ? `Window OPEN since ${group.windowOpensAt}` : `Window opens ${group.windowOpensAt}`}
    </span>
  );
}

function ReadinessDots({ r }: { r: EntryReadiness }) {
  const items: { key: keyof EntryReadiness & string; label: string }[] = [
    { key: "passport", label: "Passport" },
    { key: "flight", label: "Flight" },
    { key: "hotel", label: "Hotel" },
    { key: "contact", label: "Contact" },
  ];
  return (
    <span
      className="flex items-center gap-1"
      title={r.ready ? "Ready to file" : `Missing: ${r.missing.join(", ")}`}
    >
      {items.map((it) => (
        <span
          key={it.key}
          title={`${it.label}: ${r[it.key] ? "OK" : "missing"}`}
          className={`inline-block h-2 w-2 rounded-full ${
            r[it.key] ? "bg-emerald-500" : "bg-red-400"
          }`}
        />
      ))}
    </span>
  );
}

export default function EntryTasksPanel({ tripDateId }: { tripDateId: string }) {
  const queryClient = useQueryClient();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["entry-tasks", tripDateId],
    queryFn: () =>
      api.get<{ data: { groups: EntryGroup[] } }>(
        `/api/admin/entry-tasks?tripDateId=${tripDateId}`,
      ),
  });
  const groups = data?.data.groups ?? [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["entry-tasks", tripDateId] });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/api/admin/entry-tasks/${id}`, body),
    onSettled: invalidate,
  });

  const upload = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append("file", file);
      return uploadFormData(`/api/admin/entry-tasks/${id}/artifact`, fd);
    },
    onSettled: invalidate,
  });

  // Auto-filing trigger (Řez 3) — queued server-side; the row updates as the cloud
  // browser run finishes (refetch picks up status/artifact/lastError).
  const [queuedIds, setQueuedIds] = useState<Set<string>>(new Set());
  const runFiling = useMutation({
    mutationFn: ({ id, dry }: { id: string; dry: boolean }) =>
      api.post(`/api/admin/entry-tasks/${id}/file`, { dry }),
    onSuccess: (_data, vars) => setQueuedIds((prev) => new Set(prev).add(vars.id)),
    onSettled: invalidate,
  });

  const copySheet = async (group: EntryGroup) => {
    setSheetError(null);
    try {
      const res = await api.get<{ data: { tsv: string } }>(
        `/api/admin/entry-tasks/sheet?tripDateId=${tripDateId}&country=${group.countryCode}&kind=${group.kind}`,
      );
      await navigator.clipboard.writeText(res.data.tsv);
      const key = `${group.countryCode}|${group.kind}`;
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000);
    } catch (e) {
      setSheetError(e instanceof Error ? e.message : "Failed to load the filing sheet");
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <ClipboardList className="h-5 w-5" /> Entry documents
        </h2>
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }
  if (groups.length === 0) return null; // no online cards / e-visas for this trip's countries

  return (
    <div className="card">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <ClipboardList className="h-5 w-5" /> Entry documents
      </h2>
      <p className="mb-3 text-xs text-gray-500">
        Arrival cards & visas filed for the travellers. The shield shows the client's filing consent
        from the portal; dots = passport / flight / hotel / contact readiness.
      </p>
      {sheetError && (
        <p className="mb-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
          {sheetError}
        </p>
      )}

      <div className="space-y-5">
        {groups.map((group) => {
          const key = `${group.countryCode}|${group.kind}`;
          return (
            <div key={key}>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="font-medium text-gray-900 dark:text-white">
                  {ENTRY_TASK_KIND_LABELS[group.kind]} — {group.countryName}
                </span>
                {windowChip(group)}
                {group.groupMax && group.groupMax > 1 && (
                  <span className="text-xs text-gray-500">group of {group.groupMax}</span>
                )}
                {group.autoAvailable && group.autoChannel === "browser" && (
                  <span
                    className={`flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${
                      group.autoEnabled
                        ? "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300"
                        : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                    }`}
                    title={
                      group.autoEnabled
                        ? "Auto-filing on — the hourly cron files open tasks inside the window (consent + global switch required)"
                        : "Auto-filing off for this country — enable it in Country KB; dry runs work regardless"
                    }
                  >
                    <Bot className="h-3 w-3" /> auto {group.autoEnabled ? "on" : "off"}
                  </span>
                )}
                <span className="flex-1" />
                <button onClick={() => void copySheet(group)} className="btn-ghost btn-sm">
                  {copiedKey === key ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}{" "}
                  Copy filing sheet
                </button>
                {group.portalUrl && (
                  <a
                    href={group.portalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost btn-sm"
                    title="Open the official portal"
                  >
                    <ExternalLink className="h-4 w-4" /> Portal
                  </a>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500 dark:border-gray-700">
                      <th className="py-1.5 pr-2">Traveller</th>
                      <th className="py-1.5 pr-2">Order</th>
                      <th className="py-1.5 pr-2">Ready</th>
                      <th className="py-1.5 pr-2">Arrival</th>
                      <th className="py-1.5 pr-2">Status</th>
                      <th className="py-1.5 pr-2">Reference</th>
                      {group.autoAvailable && group.autoChannel === "browser" && (
                        <th className="py-1.5 pr-2">Auto</th>
                      )}
                      <th className="py-1.5">QR / file</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.tasks.map((row) => (
                      <tr
                        key={row.task.id}
                        className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                      >
                        <td className="py-1.5 pr-2">
                          <span className="flex items-center gap-1.5 text-gray-900 dark:text-white">
                            {row.consentAt ? (
                              <ShieldCheck
                                className="h-3.5 w-3.5 shrink-0 text-emerald-600"
                                aria-label="Filing consent given"
                              />
                            ) : (
                              <Shield
                                className="h-3.5 w-3.5 shrink-0 text-gray-300 dark:text-gray-600"
                                aria-label="No filing consent yet"
                              />
                            )}
                            {row.participantName}
                          </span>
                        </td>
                        <td className="py-1.5 pr-2 text-xs text-gray-500">{row.orderNumber}</td>
                        <td className="py-1.5 pr-2">
                          <ReadinessDots r={row.readiness} />
                        </td>
                        <td className="py-1.5 pr-2">
                          <input
                            type="date"
                            defaultValue={row.task.arrivalDate}
                            onBlur={(e) => {
                              if (e.target.value && e.target.value !== row.task.arrivalDate) {
                                patch.mutate({
                                  id: row.task.id,
                                  body: { arrivalDate: e.target.value },
                                });
                              }
                            }}
                            className="input input-sm w-36 text-xs"
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <select
                            value={row.task.status}
                            onChange={(e) =>
                              patch.mutate({ id: row.task.id, body: { status: e.target.value } })
                            }
                            className={`rounded px-1.5 py-0.5 text-xs font-medium ${STATUS_STYLES[row.task.status]}`}
                          >
                            <option value="open">open</option>
                            <option value="filed">filed</option>
                            <option value="failed">failed</option>
                            <option value="skipped">skipped</option>
                          </select>
                        </td>
                        <td className="py-1.5 pr-2">
                          <input
                            type="text"
                            defaultValue={row.task.referenceCode ?? ""}
                            placeholder="conf. no."
                            onBlur={(e) => {
                              if (e.target.value !== (row.task.referenceCode ?? "")) {
                                patch.mutate({
                                  id: row.task.id,
                                  body: { referenceCode: e.target.value || null },
                                });
                              }
                            }}
                            className="input input-sm w-28 text-xs"
                          />
                        </td>
                        {group.autoAvailable && group.autoChannel === "browser" && (
                          <td className="py-1.5 pr-2">
                            <span className="flex items-center gap-1">
                              {row.task.status === "open" && (
                                <>
                                  <button
                                    onClick={() => runFiling.mutate({ id: row.task.id, dry: true })}
                                    disabled={runFiling.isPending}
                                    className="btn-ghost btn-sm"
                                    title="Dry run — fills the whole portal form, stops before submit, stores a review screenshot"
                                  >
                                    <FlaskConical className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `File the ${group.countryName} arrival card for ${row.participantName} NOW? This submits to the government portal.`,
                                        )
                                      ) {
                                        runFiling.mutate({ id: row.task.id, dry: false });
                                      }
                                    }}
                                    disabled={
                                      runFiling.isPending ||
                                      !row.consentAt ||
                                      !row.readiness.ready ||
                                      !group.autoEnabled ||
                                      row.task.lastError?.startsWith("TIMED OUT") ||
                                      row.task.attempts > 0
                                    }
                                    className="btn-ghost btn-sm disabled:opacity-40"
                                    title={
                                      row.task.lastError?.startsWith("TIMED OUT")
                                        ? "Previous filing timed out — verify on the portal/QR, then use Retry (do not double-file)"
                                        : row.task.attempts > 0
                                          ? "A previous attempt already ran — verify on the portal/QR, then use Retry (do not double-file)"
                                          : !row.consentAt
                                            ? "Client consent missing (portal)"
                                            : !row.readiness.ready
                                              ? `Missing: ${row.readiness.missing.join(", ")}`
                                              : !group.autoEnabled
                                                ? "Auto-filing off for this country (Country KB)"
                                                : "File now via the cloud browser"
                                    }
                                  >
                                    <Play className="h-4 w-4" />
                                  </button>
                                  {(row.task.lastError?.startsWith("TIMED OUT") ||
                                    row.task.attempts > 0) && (
                                    <button
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Have you verified on the government portal/QR that ${row.participantName}'s ${group.countryName} card is NOT already filed? This re-enables filing.`,
                                          )
                                        ) {
                                          patch.mutate({
                                            id: row.task.id,
                                            body: { status: "open" },
                                          });
                                        }
                                      }}
                                      disabled={patch.isPending}
                                      className="btn-ghost btn-sm text-amber-600"
                                      title="Verified on the portal — clear the block and allow re-filing"
                                    >
                                      <RotateCcw className="h-4 w-4" />
                                    </button>
                                  )}
                                </>
                              )}
                              {queuedIds.has(row.task.id) && row.task.status === "open" && (
                                <Loader2
                                  className="h-3.5 w-3.5 animate-spin text-gray-400"
                                  aria-label="Queued — refresh in a minute"
                                />
                              )}
                              {row.task.channel === "auto" && row.task.status === "filed" && (
                                <span title="Filed automatically">
                                  <Bot className="h-4 w-4 text-violet-500" />
                                </span>
                              )}
                              {row.task.lastError && (
                                <span title={row.task.lastError}>
                                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                                </span>
                              )}
                              {row.task.attempts > 0 && (
                                <span
                                  className="text-[10px] text-gray-400"
                                  title={row.task.lastError ?? undefined}
                                >
                                  ×{row.task.attempts}
                                </span>
                              )}
                            </span>
                          </td>
                        )}
                        <td className="py-1.5">
                          <span className="flex items-center gap-1">
                            <input
                              ref={(el) => {
                                fileInputs.current[row.task.id] = el;
                              }}
                              type="file"
                              accept="image/*,application/pdf"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) upload.mutate({ id: row.task.id, file });
                                e.target.value = "";
                              }}
                            />
                            <button
                              onClick={() => fileInputs.current[row.task.id]?.click()}
                              className="btn-ghost btn-sm"
                              title={row.task.artifactFileName ?? "Upload the QR / confirmation"}
                            >
                              {upload.isPending && upload.variables?.id === row.task.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Paperclip className="h-4 w-4" />
                              )}
                            </button>
                            {row.task.artifactR2Key && (
                              <button
                                onClick={() =>
                                  void openAuthedFile(
                                    `/api/admin/entry-tasks/${row.task.id}/artifact`,
                                  )
                                }
                                className="btn-ghost btn-sm"
                                title={`Open ${row.task.artifactFileName ?? "artifact"}`}
                              >
                                <Download className="h-4 w-4 text-emerald-600" />
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
